import { FC, ReactNode, useEffect } from 'react';

import { formatCostInCents } from 'utils/clickme';
import { CampaignProductType } from 'utils/constants/campaign';
import { CURRENT_ACCOUNT_ID_STORAGE } from 'utils/constants/user';
import { FIRST_CAMPAIGN_AUCTION_PROMO } from 'utils/promoBonus';

import { ElementShownAnchor } from '@hh.ru/analytics-js';
import auctionPromoElementShown from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_promo_element_shown';
import { sessionStorageWrapper } from '@hh.ru/browser-api-utils';
import {
    Banner,
    GridColumn,
    GridRow, // exp_PORTFOLIO-58018_next_line
    Tag,
    Text,
    useBreakpoint,
    VSpacing,
} from '@hh.ru/magritte-ui';

import useTrls from 'hooks/translation/useTrls';
import { useAvailableFirstCampaignAuctionPromo } from 'hooks/useAvailableFirstCampaignAuctionPromo';

import { ProductTypeCard } from 'components/CampaignCreationWizard/PromotionTypeStep/components/ProductTypeCard/ProductTypeCard';
import { SettingsModeCard } from 'components/CampaignCreationWizard/PromotionTypeStep/components/SettingsModeCard/SettingsModeCard';

import {
    selectWizardCampaignOptions,
    selectWizardCampaignOptionsIsLoading,
    selectWizardCpcMode,
    selectWizardSelectedProductType,
    selectWizardSelectedVacancyId,
    setWizardCpcMode,
    setWizardSelectedProductType,
    WizardCpcMode,
} from 'store/models/campaignCreationWizard';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    active: 'CampaignCreationWizard.PromotionTypeStep.active',
    minBudget: 'CampaignCreationWizard.PromotionTypeStep.minBudget',
    topSearch: {
        title: 'CampaignCreationWizard.PromotionTypeStep.topSearch.title',
        description: 'CampaignCreationWizard.PromotionTypeStep.topSearch.description',
        promoTag: 'CampaignCreationWizard.PromotionTypeStep.topSearch.promoTag',
    },
    cpc: {
        title: 'CampaignCreationWizard.PromotionTypeStep.cpc.title',
        description: 'CampaignCreationWizard.PromotionTypeStep.cpc.description',
    },
    // exp_PORTFOLIO-58018_start
    vr: {
        title: 'CampaignCreationWizard.PromotionTypeStep.vr.title',
        description: 'CampaignCreationWizard.PromotionTypeStep.vr.description',
        price: 'CampaignCreationWizard.PromotionTypeStep.vr.price',
        tag: 'CampaignCreationWizard.PromotionTypeStep.vr.tag',
    },
    // exp_PORTFOLIO-58018_end
    auctionWarning: {
        title: 'adCreateMenu.productType.auction.warningTitle',
        description: 'adCreateMenu.productType.auction.warningDescription',
    },
    cpcSettingsMode: {
        label: 'CampaignCreationWizard.PromotionTypeStep.cpcSettingsMode.label',
        quick: {
            title: 'CampaignCreationWizard.PromotionTypeStep.cpcSettingsMode.quick.title',
            description: 'CampaignCreationWizard.PromotionTypeStep.cpcSettingsMode.quick.description',
        },
        flexible: {
            title: 'CampaignCreationWizard.PromotionTypeStep.cpcSettingsMode.flexible.title',
            description: 'CampaignCreationWizard.PromotionTypeStep.cpcSettingsMode.flexible.description',
        },
    },
};

// exp_PORTFOLIO-58018_start
const CARD_CONFIG: Record<
    CampaignProductType,
    { titleKey: string; descriptionKey: string; priceKey?: string; tag?: string }
> = {
    // exp_PORTFOLIO-58018_end
    [CampaignProductType.SearchAuction]: {
        titleKey: TrlKeys.topSearch.title,
        descriptionKey: TrlKeys.topSearch.description,
    },
    [CampaignProductType.Cpc]: {
        titleKey: TrlKeys.cpc.title,
        descriptionKey: TrlKeys.cpc.description,
    },
    // exp_PORTFOLIO-58018_start
    [CampaignProductType.Vr]: {
        titleKey: TrlKeys.vr.title,
        descriptionKey: TrlKeys.vr.description,
        priceKey: TrlKeys.vr.price,
        tag: TrlKeys.vr.tag,
    },
    // exp_PORTFOLIO-58018_end
};

const SETTINGS_MODES = [WizardCpcMode.Quick, WizardCpcMode.Flexible];

const SETTINGS_MODE_CONFIG: Record<WizardCpcMode, { titleKey: string; descriptionKey: string }> = {
    [WizardCpcMode.Quick]: {
        titleKey: TrlKeys.cpcSettingsMode.quick.title,
        descriptionKey: TrlKeys.cpcSettingsMode.quick.description,
    },
    [WizardCpcMode.Flexible]: {
        titleKey: TrlKeys.cpcSettingsMode.flexible.title,
        descriptionKey: TrlKeys.cpcSettingsMode.flexible.description,
    },
};

export const PromotionTypeStep: FC = () => {
    const dispatch = useDispatch();
    const campaignOptions = useSelector(selectWizardCampaignOptions);
    const campaignOptionsIsLoading = useSelector(selectWizardCampaignOptionsIsLoading);
    const selectedProductType = useSelector(selectWizardSelectedProductType);
    const selectedCpcMode = useSelector(selectWizardCpcMode);
    const selectedVacancyId = useSelector(selectWizardSelectedVacancyId);
    const trl = useTrls();
    const { isGtM } = useBreakpoint();
    const { isPromoAvailable, promoEndDate, promoSumPercent } = useAvailableFirstCampaignAuctionPromo();

    const availableProductTypes = campaignOptions?.productTypes;

    useEffect(() => {
        if (!availableProductTypes?.length) {
            if (selectedProductType !== null) {
                dispatch(setWizardSelectedProductType(null));
            }
            return;
        }
        if (selectedProductType === null || !availableProductTypes.includes(selectedProductType)) {
            dispatch(setWizardSelectedProductType(availableProductTypes[0]));
        }
    }, [availableProductTypes, dispatch, selectedProductType]);

    useEffect(() => {
        if (selectedProductType === CampaignProductType.Cpc && selectedCpcMode === null) {
            dispatch(setWizardCpcMode(WizardCpcMode.Quick));
        }
    }, [dispatch, selectedCpcMode, selectedProductType]);

    const isCpcSelected = selectedProductType === CampaignProductType.Cpc;
    const activeLabel = trl(TrlKeys.active);

    const currentAccountId = sessionStorageWrapper.getItem(CURRENT_ACCOUNT_ID_STORAGE);
    const auctionCampaign = campaignOptions?.auctionProductData?.auctionCampaign;

    const isProductActive = (productType: CampaignProductType): boolean => {
        if (productType === CampaignProductType.SearchAuction) {
            return auctionCampaign != null;
        }
        return false;
    };

    const getProductPrice = (productType: CampaignProductType, priceKey?: string): string => {
        if (productType === CampaignProductType.SearchAuction && campaignOptions?.auctionProductData) {
            return trl(TrlKeys.minBudget, [formatCostInCents(campaignOptions.auctionProductData.budget.minValue)]);
        }
        if (productType === CampaignProductType.Cpc && campaignOptions?.cpcProductData) {
            return trl(TrlKeys.minBudget, [formatCostInCents(campaignOptions.cpcProductData.budget.minValue)]);
        }
        return priceKey ? trl(priceKey) : '';
    };

    const isAuctionWarning = Boolean(
        selectedProductType === CampaignProductType.SearchAuction &&
            isProductActive(CampaignProductType.SearchAuction) &&
            currentAccountId &&
            auctionCampaign?.campaignAccountInfo.id !== Number(currentAccountId)
    );

    return (
        <GridColumn xs={4} s={8} m={12}>
            <GridRow rowGap={isGtM ? 24 : 12}>
                {availableProductTypes?.map((productType) => {
                    // exp_PORTFOLIO-58018_start
                    const { titleKey, descriptionKey, priceKey, tag } = CARD_CONFIG[productType];
                    const active = isProductActive(productType);
                    const isAuction = productType === CampaignProductType.SearchAuction;
                    const isVr = productType === CampaignProductType.Vr;
                    const isVrProductExist = availableProductTypes.includes(CampaignProductType.Vr);
                    let productTag: ReactNode;
                    if (isVr) {
                        productTag = <Tag style="positive">{trl(tag)}</Tag>;
                    } else if (isAuction && isPromoAvailable) {
                        productTag = (
                            <ElementShownAnchor
                                fn={auctionPromoElementShown}
                                vacancyId={String(selectedVacancyId)}
                                promoKey={FIRST_CAMPAIGN_AUCTION_PROMO}
                                triggerType="TAG"
                            >
                                <Tag style="positive">
                                    {trl(TrlKeys.topSearch.promoTag, [promoSumPercent, promoEndDate])}
                                </Tag>
                            </ElementShownAnchor>
                        );
                    }
                    // exp_PORTFOLIO-58018_end
                    return (
                        <GridColumn xs={4} s={4} m={4} l={4} xl={4} xxl={4} key={productType} flexibleContent>
                            <ProductTypeCard
                                productType={productType}
                                title={trl(titleKey)}
                                description={trl(descriptionKey)}
                                price={getProductPrice(productType, priceKey)}
                                checked={selectedProductType === productType}
                                active={active}
                                activeLabel={activeLabel}
                                isStatusLoading={
                                    productType === CampaignProductType.SearchAuction && campaignOptionsIsLoading
                                }
                                // exp_PORTFOLIO-58018_start
                                tag={productTag}
                                isVrProductExist={isVrProductExist}
                                // exp_PORTFOLIO-58018_end
                            />
                        </GridColumn>
                    );
                })}
            </GridRow>
            {isAuctionWarning && (
                <>
                    <VSpacing default={20} />
                    <Banner
                        showClose={false}
                        style="warning-secondary"
                        title={
                            <Text typography="title-5-semibold">
                                {trl(TrlKeys.auctionWarning.title, [auctionCampaign?.campaignAccountInfo.title])}
                            </Text>
                        }
                        content={
                            <Text typography="paragraph-2-regular">{trl(TrlKeys.auctionWarning.description)}</Text>
                        }
                        data-qa="promotion-type-step-auction-warning"
                    />
                </>
            )}
            {isCpcSelected && (
                <>
                    <VSpacing default={32} />
                    <Text typography="title-4-semibold">{trl(TrlKeys.cpcSettingsMode.label)}</Text>
                    <VSpacing default={16} />
                    <GridRow rowGap={isGtM ? 24 : 12}>
                        {SETTINGS_MODES.map((mode) => {
                            const { titleKey, descriptionKey } = SETTINGS_MODE_CONFIG[mode];
                            return (
                                <GridColumn xs={4} s={4} m={4} l={4} xl={4} xxl={4} key={mode} flexibleContent>
                                    <SettingsModeCard
                                        mode={mode}
                                        title={trl(titleKey)}
                                        description={trl(descriptionKey)}
                                        checked={selectedCpcMode === mode}
                                    />
                                </GridColumn>
                            );
                        })}
                    </GridRow>
                </>
            )}
            <VSpacing default={32} />
        </GridColumn>
    );
};
